import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ResumeImg from "../../assets/women-8080878_1280.png";
import { useDispatch } from "react-redux";
import ForgotPasswordViewController from "../../redux/actions/ForgotPasswordApiAction";
import logo from "../../assets/Thryiv_logo_tm_TB.png";

/* OtpFields Component */
const OtpFields = ({ control }) => {
  const inputs = useRef([]);

  const handleChange = (e, index, field) => {
    const val = e.target.value.replace(/\D/g, "");
    field.onChange(val);
    if (val && index < 5) inputs.current[index + 1].focus();
    if (val === "" && index > 0) inputs.current[index - 1].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  return (
    <Box display="flex" justifyContent="center" gap={1.5} mb={3}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Controller
          key={i}
          name={`otp${i}`}
          control={control}
          defaultValue=""
          rules={{ required: true, pattern: /^\d$/ }}
          render={({ field }) => (
            <TextField
              {...field}
              inputRef={(el) => (inputs.current[i] = el)}
              onChange={(e) => handleChange(e, i, field)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: "1.3rem",
                  width: 40,
                  height: 45,
                  padding: 0,
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                },
              }}
              variant="outlined"
            />
          )}
        />
      ))}
    </Box>
  );
};

/* Main ForgotPassword Component */
const ForgotPassword = () => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const [step, setStep] = useState("email"); // "email" | "otp"
  const [showPassword, setShowPassword] = useState(false);
  const [timer, setTimer] = useState(30);
  const [showResend, setShowResend] = useState(false);
  const [emailForReset, setEmailForReset] = useState("");
  const dispatch = useDispatch();
  const { forgotPassword, resetOtpPassword, resendOtp } =
    ForgotPasswordViewController(); // get the API function

  const requestOtp = async ({ identifier }) => {
    try {
      await dispatch(forgotPassword({ identifier }));
      setEmailForReset(identifier);
      setStep("otp");
      setShowResend(false);
      setTimer(30);

      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev === 1) {
            clearInterval(interval);
            setShowResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("OTP request failed", error);
    }
  };

  const resetPassword = async (data) => {
    const { password, confirmPassword, identifier } = data;

    if (password !== confirmPassword) {
      return;
    }

    const otp = Array.from({ length: 6 }, (_, i) => data[`otp${i}`]).join("");

    try {
      await dispatch(
        resetOtpPassword({
          identifier, // email
          otp,
          password,
        })
      );
      reset();
      navigate("/login");
    } catch (error) {
      console.error("Reset password failed", error);
    }
  };

  const handleResendOtp = async () => {
    if (!emailForReset) {
      return;
    }

    try {
      await dispatch(resendOtp({ identifier: emailForReset }));
      setTimer(30);
      setShowResend(false);

      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev === 1) {
            clearInterval(interval);
            setShowResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Resend OTP failed:", error);
    }
  };

  return (
    <Grid container sx={{ height: "100vh", width: "100vw" }}>
      {/* LEFT PANEL */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          position: "relative", // To contain the overlay
          color: "#fff",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          textAlign: "center",
          p: 4,
          minHeight: "50vh",
          backgroundImage: `url(${ResumeImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0, 0, 0, 0.6)", // Black overlay with 60% opacity
            zIndex: 1,
          }}
        />

        {/* Content */}
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "1.8rem", md: "2.4rem" },
            }}
          >
            Empowering people through seamless HR management.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 2,
              maxWidth: "80%",
              fontSize: { xs: "0.9rem", md: "1rem" },
            }}
          >
            Efficiently manage your workforce, streamline operations
            effortlessly.
          </Typography>
        </Box>
      </Grid>

      {/* RIGHT PANEL */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          backgroundColor: "#fff",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Paper sx={{ p: 4, boxShadow: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="center" alignItems="center">
              <img
                src={logo}
                alt="Company Logo"
                style={{
                  height: 60, // adjust height as needed
                  objectFit: "contain", // keeps aspect ratio
                }}
              />
            </Box>
            <Typography
              variant="h6"
              align="center"
              sx={{ mt: 2, fontWeight: "bold", mb: 2 }}
            >
              Forgot Password?
            </Typography>

            {step === "email" ? (
              <>
                <Typography
                  variant="body2"
                  align="center"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  No worries, we’ll send you reset instructions.
                </Typography>
                <form onSubmit={handleSubmit(requestOtp)}>
                  <Controller
                    name="identifier"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Enter a valid email",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Enter Email Address"
                        fullWidth
                        error={!!errors.identifier}
                        helperText={errors.identifier?.message}
                        variant="outlined"
                        autoComplete="email"
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlined />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      backgroundColor: "#FF6A00",
                      "&:hover": { backgroundColor: "#E65A00" },
                      mb: 1,
                      py: 1.4,
                    }}
                  >
                    Get OTP
                  </Button>

                  {/* Back to Login - Right aligned */}
                  <Typography
                    variant="body2"
                    align="right"
                    sx={{
                      color: "#007bff",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => navigate("/login")}
                  >
                    Back to Login?
                  </Typography>
                </form>
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  align="center"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Enter the 6-digit code sent to your email.
                </Typography>
                <form onSubmit={handleSubmit(resetPassword)}>
                  <OtpFields control={control} />

                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: true, minLength: 6 }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlined />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="confirmPassword"
                    control={control}
                    rules={{ required: true, minLength: 6 }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Confirm Password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlined />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      backgroundColor: "#FF6A00",
                      "&:hover": { backgroundColor: "#E65A00" },
                      mb: 2,
                      py: 1.4,
                    }}
                  >
                    Reset Password
                  </Button>
                  <Typography variant="body2" align="center" sx={{ mb: 1 }}>
                    Didn’t receive the OTP?{" "}
                    {showResend ? (
                      <Box
                        component="span"
                        sx={{ fontWeight: "bold", cursor: "pointer" }}
                        onClick={handleResendOtp}
                      >
                        Resend OTP
                      </Box>
                    ) : (
                      <Box
                        component="span"
                        sx={{ fontWeight: "bold", color: "gray" }}
                      >
                        Resend OTP in {timer} s
                      </Box>
                    )}
                  </Typography>

                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      color: "#007bff",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => navigate("/login")}
                  >
                    Back to Login?
                  </Typography>
                </form>
              </>
            )}
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
